<template>
  <DetailLayoutFullWidth>
    <template slot="header">
      <BaseHeaderFinancialDashboard
        :title="$t('invoices.issued_header')"
        :disableSelect="disableSelect"
        @change="handleChangeEvent"
        @refresh="refreshPage"
        @print="printDashboard"
      />
    </template>
    <template slot="main">
      <loading :opacity="darkMode ? 0 : 0.5" :active="isGeneralLoading" :is-full-page="false" :z-index="10" color="var(--primary-DEFAULT)"></loading>
      <div class="print-area mt-2 md:mt-6 space-y-5">
        <div class="flex flex-wrap gap-4"> 
          <single-panel class="w-full flex-1 min-w-max" :class="isNegativeValue.issued" :header="$t('financial_dashboard.total_invoices_issued')" :content="totalEmitidas">
            <template slot="footer">
              <span :class="isNegativeValue.Subtitulo1" class="font-semibold">{{ totalSubtitleEmitidas }}</span>
              <span> {{ $t('financial_dashboard.vs_the_previous_period', { period: dataPeriodSubtitle }) }}</span>
            </template>
          </single-panel>
          <single-panel class="w-full flex-1 min-w-max" :class="isNegativeValue.total" :header="$t('verifactu.total_amount')" :content="importeEmitidas">
            <template slot="footer">
              <span>{{ $t('invoices.taxable_base_short') }}: <span :class="isNegativeValue.Subtitulo2P1" class="font-semibold">{{ baseImponibleEmitidas }}</span></span>
              |
              <span>{{ $t('impuestos.header') }}: <span :class="isNegativeValue.Subtitulo2P2" class="font-semibold">{{ impuestosEmitidas }}</span></span>
            </template>
          </single-panel>
          <single-panel class="w-full flex-1 min-w-max" :class="isNegativeValue.ticket"  :header="$t('financial_dashboard.average_ticket')" :content="ticketEmitidas">
            <template slot="footer">
              <span :class="isNegativeValue.Subtitulo3" class="font-semibold">{{ totalTicketMedio }}</span>
              <span> {{ $t('financial_dashboard.vs_the_previous_period', { period: dataPeriodSubtitle }) }}</span>
            </template>
          </single-panel>
          <single-panel class="w-full flex-1 min-w-max content-red-500" :header="$t('tipos.origen.rectificativa')" :content="rectificativasEmitidas">
            <template slot="footer">
              <span>{{ $t('invoices.importe') }}: <span :class="isNegativeValue.Subtitulo4" class="font-semibold">{{ totalRectificativasSubtitle }}</span></span>              
            </template>
          </single-panel>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <BasePanel class="md:col-span-2">
            <template slot="header">
              <h3 class="text-base text-secondary-800 dark:text-darkblue-100 font-medium">{{ $t('financial_dashboard.issued_evolution') }} ({{ dataPeriod }})</h3>
            </template>
            <template slot="content">
              <div class="w-full overflow-hidden">
                <!-- TODO: ¿Reemplazar con leyenda personalizada? -->
                <Chart id="chart-panel" 
                  :ref="refPanelChart"
                  type="bar"
                  :stacked="true"
                  :expand="true"
                  :charData="chartBarDataStatus"
                  :parse-format="formatValueDate"
                  :x-axis-value-format="formatChartDate"
                  y-axis-value-format="compact"
                  tooltip-y-value-format="currency"
                  y-axis-suffix="€"
                />
              </div>
            </template>
          </BasePanel>
          <BasePanel>
            <template slot="header">
              <h3 class="text-base text-secondary-800 dark:text-darkblue-100 font-medium">{{ $t('financial_dashboard.tax_distribution') }}</h3>
            </template>
            <template slot="content">
              <div v-if="!desgloseImpuestos.length" class="text-secondary-500 dark:text-darkblue-300 italic">
                {{ $t('app.no_data_text') }}
              </div>
              <template v-else>
                <div class="mb-6 md:mb-2">
                  <Chart id="chart-doughnut" 
                    :ref="refDoughnutChart"
                    type="doughnut"
                    :expand="false"
                    :show-legend="false"
                    class="mb-2"
                    :charData="chartDonutDataStatus"
                    tooltip-value-format="currency"
                    tooltip-suffix="€"
                  />
                </div>
                <div class="mt-5">
                  <div v-for="(tax, index) in desgloseImpuestos" :key="index" class="px-2">
                    <div class="flex flex-wrap justify-between">
                      <div 
                        @click="toggleDatasetChart(refDoughnutChart, index)" 
                        class="flex items-center gap-1 text-base cursor-pointer" 
                        :style="{ textDecoration: isDatasetHidden(refDoughnutChart, index) ? 'line-through' : 'none' }">
                        <base-dot-icon class="m-auto mr-1" :style="{ color: dotColor(index) }" />
                        <span>{{ tax.label }}</span>
                      </div>
                      <div class="flex gap-4">
                        <span class="font-bold">
                          {{ $n(tax.value, 'currency') }} € ({{ $n(tax.porcentaje, 'percent_decimal') }})
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </template>
          </BasePanel>
        </div>

        <BasePanel id="break_table">
          <template v-slot:header>
            <div class="flex items-center">
              <div class="text-lg font-semibold whitespace-nowrap dark:text-darkblue-200" >{{ $t("financial_dashboard.most_recent_invoices_issued") }}</div>
              <div class="w-full flex justify-end">
                <base-button-responsive
                  mode="primary-phantom"
                  size="xs"
                  icon="eye"
                  :text="$t('app.see_all')"
                  :disabled="disableSelect"
                  @click="goFacturasEmitidas"
                  class="hide-button min-w-40 whitespace-nowrap"
                />
              </div>
            </div>
          </template>
          <template v-slot:content>
            <div class="overflow-x-auto">
              <el-table
                ref="summaryTable"
                class="border-primary-100 dark:text-primary-500 dark:border-darkblue-50 min-w-max shadow-sm"
                :data="dataTable"
                style="table-layout:auto">
                <template v-for="(label, prop) in tableColumns">
                  <el-table-column v-if="prop !== 'EstadoVerifactu' || hasVerifactu"
                    :key="prop"
                    :prop="prop"
                    :formatter="['Base', 'Impuestos', 'Total'].includes(prop) ? formatCellValue : undefined"
                    :min-width="maxColumnWidth[prop]"
                    :label="label">
                  </el-table-column>
                </template>
              </el-table>
            </div>
          </template>
        </BasePanel>
      </div>
    </template>
  </DetailLayoutFullWidth>
</template>

<script>
import moment from 'moment'
import { i18n } from '@/i18n'
import _ from 'lodash'
import { COLORES_DASHBOARD } from '@/config/colores-dashboard'
import colorsMixin from '@/mixins/colorsMixin'

import Loading from 'vue-loading-overlay'
import DetailLayoutFullWidth from '@/components/layout/DetailLayoutFullWidth.vue'
import SinglePanel from '@/components/SinglePanel.vue'
import Chart from '@/components/charts/Chart.vue'

import { getDashboardFinancieroEmitidas } from '@/services/dashboard/getDashboardFinancieroEmitidas'
import { getDashboardFacturasPag } from '@/services/dashboard/getDashboardFacturasPag'

export default {
  name: 'financial-dashboard',
  mixins: [colorsMixin],
  components: {
    DetailLayoutFullWidth,
    SinglePanel,
    Loading,
    Chart
  },
  props: {},
  data () {
    return {
      refPanelChart: 'chartPanel',
      refDoughnutChart: 'doughnutChart',
      data: [],
      chartBarDataStatus: null,
      chartDonutDataStatus: null,
      financialEmitidas: {
        FacturasEmitidas: [],
      },
      tableColumns: {
        NumFactura: i18n.t('invoices.num_factura'),
        Fecha: i18n.t('app.date'),
        Cliente: i18n.t('clients.entity_name'),
        Base: i18n.t('invoices.importe'),
        Impuestos: i18n.t('impuestos.header'),
        Total: i18n.t('app.total'),
        Estado: i18n.t('app.status'),
        EstadoVerifactu: i18n.t('wizard.step_verifactu_title'),
      },
      tableFontStyle: {
        font: '',
        letterSpacing: 0,
        paddingX: 0
      }
    }
  },
  computed: {
    id () {
      return this.$route.params.id
    },
    clientId () {
      return this.$store.getters['dashboard/getSelectedClient'] || null
    },
    disableSelect () {
      return !this.cifEmpresa || this.isGeneralLoading 
    },
    tiposCodigoImpuesto: function () {
      return this.$store.getters['config/TIPOS_CODIGO_IMPUESTO']
    },
    activePeriod () {
      return this.$store.getters['financialdashboard/activePeriod']
    },
    isNegativeValue () {
      const isNegativeSubtitle1 = this.dataPerPeriodEmitido?.Subtitulos?.Subtitulo1 < 0
      const isNegativeSubtitle2P1 = this.dataPerPeriodEmitido?.BaseImponible < 0
      const isNegativeSubtitle2P2 = this.dataPerPeriodEmitido?.TotalImpuestos < 0
      const isNegativeSubtitle3 = this.dataPerPeriodEmitido?.Subtitulos?.Subtitulo3 < 0
      const isNegativeSubtitle4 = this.dataPerPeriodEmitido?.Subtitulos?.Subtitulo4 < 0
      return {
        issued: { 'content-red-700': this.dataPerPeriodEmitido?.Total < 0 },
        Subtitulo1: { 'text-red-500': isNegativeSubtitle1, 'text-primary-800 dark:text-primary-400': !isNegativeSubtitle1 },
        total: { 'content-red-700': this.dataPerPeriodEmitido?.ImporteTotal < 0 },
        Subtitulo2P1: { 'text-red-500': isNegativeSubtitle2P1, 'text-primary-800 dark:text-primary-400': !isNegativeSubtitle2P1 },
        Subtitulo2P2: { 'text-red-500': isNegativeSubtitle2P2, 'text-primary-800 dark:text-primary-400': !isNegativeSubtitle2P2 },
        ticket: { 'content-red-700': this.dataPerPeriodEmitido?.TicketMedio < 0 },
        Subtitulo3: { 'text-red-500': isNegativeSubtitle3, 'text-primary-800 dark:text-primary-400': !isNegativeSubtitle3 },
        Subtitulo4: { 'text-red-500': isNegativeSubtitle4, 'text-primary-800 dark:text-primary-400': !isNegativeSubtitle4 },
      }
    },
    dataPeriodSubtitle () {
      return this.activePeriods.find(p => p.id === this.activePeriod)?.nameSingular.toLowerCase() ?? this.activePeriod
    },
    cifEmpresa () {
      return this.$store.getters['financialdashboard/cifEmpresa']
    },
    valueDate () {
      return this.$store.getters['financialdashboard/valueDate']
    },
    currentFormat () {
      return this.$store.getters['financialdashboard/currentFormat']
    },
    isGeneralLoading: function () {
      return this.$store.getters.isLoading
    },
    isDatasetHidden () {
      return this.$store.getters['financialdashboard/isDatasetHidden']
    },
    formatChartDate () {
      return this.currentFormat.i18nFormat || this.currentFormat.momentDate
    },
    formatValueDate () {
      return this.currentFormat.momentValueDate
    },
    dataPerPeriodEmitido () {
      let picker = this.valueDate
      if (this.activePeriod === 'semana') {
        picker = moment(picker).format('[Semana] W')
      }
      if (!picker) {
        return this.financialEmitidas.FacturasEmitidas.at(-1)
      }
      return this.financialEmitidas.FacturasEmitidas.find((p) => p.Fecha === picker)
    },
    dataTable () {
      return this.data
    },
    totalEmitidas () {
      return i18n.n(this.dataPerPeriodEmitido?.Total ?? 0, 'integer_group')
    },
    totalSubtitleEmitidas () {
      return `${i18n.n(this.dataPerPeriodEmitido?.Subtitulos?.Subtitulo1 ?? 0, 'percent_decimal')}`
    },
    impuestosEmitidas () {
      return `${i18n.n(this.dataPerPeriodEmitido?.TotalImpuestos ?? 0, 'currency')} €`
    },
    importeEmitidas () {
      return `${i18n.n(this.dataPerPeriodEmitido?.ImporteTotal ?? 0, 'currency')} €`
    },
    baseImponibleEmitidas () {
      return `${i18n.n(this.dataPerPeriodEmitido?.BaseImponible ?? 0, 'currency')} €`
    },
    ticketEmitidas () {
      return `${i18n.n(this.dataPerPeriodEmitido?.TicketMedio ?? 0, 'currency')} €`
    },
    totalTicketMedio () {
      return `${i18n.n(this.dataPerPeriodEmitido?.Subtitulos?.Subtitulo3 ?? 0, 'percent_decimal')}`
    },
    rectificativasEmitidas () {
      return i18n.n(this.dataPerPeriodEmitido?.Rectificativas ?? 0, 'integer_group')
    },
    totalRectificativasSubtitle () {
      return `${i18n.n(this.dataPerPeriodEmitido?.Subtitulos?.Subtitulo4 ?? 0, 'integer_group')} €`
    },
    desgloseImpuestos () {
      const result = []
      const breakdown = this.dataPerPeriodEmitido?.DesgloseImpuestos
      if (!Array.isArray(breakdown) || breakdown.length === 0 || !this.chartBarDataStatus?.datasets?.length) {
        return result
      }

      for (let i = 0; i < breakdown.length; i++) {
        const tax = breakdown[i]
        const chartTaxColor = this.chartBarDataStatus.datasets.find(ds => ds.clave === tax.Clave)?.backgroundColor || this.labelColor(i)
        result.push({
          clave: tax.Clave,
          label: tax.Label,
          value: tax.Total,
          color: chartTaxColor,
          porcentaje: tax.Porcentaje,
        })
      }
      return result
    },
    darkMode: function () {
      return this.$store.getters['app/darkMode']
    },
    isLower1024: function () {
      return this.$store.getters['app/getIsLower1024']
    },
    tiposEstado: function () {
      return this.$store.getters['config/TIPOS_ESTADO']
    },
    activePeriods: function () {
      return this.$store.getters['config/ACTIVE_PERIODS']
    },
    dataPeriod () {
      return this.activePeriods.find(p => p.id === this.activePeriod)?.timePeriod ?? this.activePeriod
    },
    tiposEstadoVerifactu: function () {
      return this.$store.getters['config/TIPOS_ESTADO_VERIFACTU_DASHBOARD']
    },
    enabledDates () {
      return this.financialEmitidas.FacturasEmitidas.map((df) => df.Fecha)
    },
    hasVerifactu () {
      return this.$store.getters['auth/hasVerifactuModule']
    },
    maxColumnWidth () {
      const max = {}
      Object.entries(this.tableColumns).forEach(([prop, label]) => {
        max[prop] = this.textWidthPx(label)
      })
      this.dataTable.forEach(row => {
        Object.keys(max).forEach(key => {
          let formattedValue = row[key]
          if (['Base', 'Impuestos', 'Total'].includes(key)) {
            formattedValue = i18n.n(row[key] ?? 0, 'currency') + ' €'
          } 
          max[key] = Math.max(max[key], this.textWidthPx(formattedValue))
        })
      })
      return max
    },
  },
  created: async function () {
    this._measureCanvas = null
  },
  mounted () {
    this.$nextTick(this.readTableFont)
  },
  watch: {},
  methods: {
    labelColor (index) {
      return COLORES_DASHBOARD[index] || 'transparent'
    },
    dotColor (index) {
      return this.chartDonutDataStatus?.datasets[0]?.backgroundColor[index] || 'transparent'
    },
    goFacturasEmitidas () {
      // cambiar filtro entero mas adelante (filtra por cliente queremos por cif empresa)
      const filter = {
        type: 'empresa',
        data: {
          label: this.cifEmpresa?.RazonSocial,
          id: this.cifEmpresa?.IdCIFEmpresa,
        },
      }
      let params
      if (this.cifEmpresa) {
        params = { data: { filters: [filter] } }
      }
      this.$router.push({ name: 'invoicesissued', params })
    },
    async getDashboardFinancieroEmitidas () {
      try {
        this.$store.commit('setIsLoading', true)
        this.financialEmitidas.FacturasEmitidas = []
        const unitTime = this.$store.getters['financialdashboard/momentTimeUnit']
        const params = {
          idCifEmpresa: this.cifEmpresa?.IdCIFEmpresa ?? null,
          fechaFiltro: this.activePeriod,
          FechaFin: moment(this.valueDate, this.currentFormat.momentValueDate).startOf('day').endOf(unitTime).format('YYYY-MM-DDT[00:00:00]'),
        }
        const dataDashboard = await getDashboardFinancieroEmitidas(params)
        if (!dataDashboard.blnError) {
          if (dataDashboard.FacturasEmitidas) {
            for (const data of dataDashboard.FacturasEmitidas) {
              this.financialEmitidas.FacturasEmitidas.push(this.getFacturaEmitidaContent(data))
            }
          }
          setTimeout(() => {
            this.loadChartData()
          }, 100)
          setTimeout(() => {
            this.loadDoughnutChartData()
          }, 200)
        } else {
          this.doFeedback(
            i18n.t('feedback.error.formated_api_response_error', dataDashboard)
          )
        }
      } catch (error) {
        console.log('CustomLog: error', error)
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },
    getFacturaEmitidaContent (item) {
      return {
        Rectificativas: item.Rectificativas,
        TicketMedio: item.TicketMedio,
        ImporteTotal: item.ImporteTotal,
        Subtitulos: item.Subtitulos,
        Fecha: item.Fecha,
        Total: item.Total,
        BaseImponible: item.BaseImponible,
        PendientePago: item.PendientePago,
        TotalImpuestos: item.TotalImpuestos,
        DesgloseImpuestos: item.DesgloseImpuestos.map(this.getDesgloseImpuestosContent).sort(this.sortDesgloseImpuestos)
      }
    },
    sortDesgloseImpuestos (a, b) {
      return parseInt(a.Clase) - parseInt(b.Clase) || parseInt(b.Tipo) - parseInt(a.Tipo)
    },
    getDesgloseImpuestosKey (item) {
      return `${item.Clase}-${item.Tipo}`
    },
    getDesgloseImpuestosContent (item) {
      const taxType = this.tiposCodigoImpuesto.find(t => t.id === item.Clase)?.short ?? item.Clase
      return {
        Clave: this.getDesgloseImpuestosKey(item),
        Label: `${taxType} ${i18n.n(item.Tipo ?? 0, 'percent_decimal')}`,
        Clase: item.Clase,
        Tipo: item.Tipo,
        Total: item.Total,
        Porcentaje: item.Porcentaje
      }
    },
    loadChartData () {
      this.chartBarDataStatus = {
        labels: [],
        datasets: [
          {
            label: i18n.t('invoices.taxable_base'),
            data: [],
            // TODO: Nueva paleta de colores para el dashboard?
            // TODO: Personalización de esta paleta desde configuración cliente?
            backgroundColor: this.getColor('--primary-DEFAULT'),
          },
        ]
      }
      const uniqueTaxes = _.uniqBy(this.financialEmitidas.FacturasEmitidas.flatMap(invoice => invoice.DesgloseImpuestos), 'Clave').sort(this.sortDesgloseImpuestos)
      const datasetTaxes = []
      for (let i = 0; i < uniqueTaxes.length; i++) {
        const tax = uniqueTaxes[i]
        datasetTaxes.push({
          clave: tax.Clave,
          label: tax.Label,
          data: [],
          backgroundColor: this.labelColor(i),
          tipo: tax.Tipo,
        })
      }
      if (datasetTaxes) {
        this.chartBarDataStatus.datasets.push(...datasetTaxes)
      }

      this.financialEmitidas.FacturasEmitidas.forEach(element => {
        this.chartBarDataStatus.labels.push(element.Fecha)
        this.chartBarDataStatus.datasets[0].data.push(element.BaseImponible)

        if (this.chartBarDataStatus.datasets.length > 1) {
          for (let index = 1; index < this.chartBarDataStatus.datasets.length; index++) {
            const dataSet = this.chartBarDataStatus.datasets[index]
            dataSet.data.push(element.DesgloseImpuestos.find(tax => tax.Clave === dataSet.clave)?.Total ?? 0)
          }
        }
      })
    },
    loadDoughnutChartData () {
      this.chartDonutDataStatus = {
        labels: [],
        datasets: [
          {
            label: [],
            data: [],
            borderWidth: 0,
            backgroundColor: [],
          }
        ]
      }
      this.desgloseImpuestos.forEach(({ label, value, color }) => {     
        this.chartDonutDataStatus.labels.push(label)
        this.chartDonutDataStatus.datasets[0].backgroundColor.push(color)
        this.chartDonutDataStatus.datasets[0].data.push(value)
      })
    },
    async getTableInfo () {
      try {
        this.$store.commit('setIsLoading', true)
        this.data = []
        const params = {
          idCifEmpresa: this.cifEmpresa?.IdCIFEmpresa ?? null,
        }
        const dataTable = await getDashboardFacturasPag(params)
        if (!dataTable.blnError) {
          for (const data of dataTable.Response) {
            data.Fecha = i18n.d(data.Fecha, 'numeric_date', { date: '-', })
            data.Estado = this.tiposEstado.find((obj) => obj.id === data.Estado)?.Nombre ?? data.Estado
            data.EstadoVerifactu = this.tiposEstadoVerifactu.find((obj) => obj.id === data.EstadoVerifactu)?.Nombre ?? data.EstadoVerifactu
            this.data.push(data)
          }
        } else {
          this.doFeedback(i18n.t('feedback.error.formated_api_response_error', dataTable))
        }
      } catch (error) {
        this.doFeedback()
        console.error(error)
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },
    handleChangeEvent ({ field, value, refresh }) {
      if (refresh) {
        if (field !== 'valueDate' || !this.enabledDates.includes(value)) {
          this.refreshPage()
        } else {
          setTimeout(() => {
            // this.loadChartData()
            this.loadDoughnutChartData()
          }, 100)
        }
      }
    }, 
    async refreshPage () {
      await this.getDashboardFinancieroEmitidas()
      await this.getTableInfo()
    },
    toggleDatasetChart (refChart, datasetIndex) {
      this.$store.commit('financialdashboard/toggleHiddenDataset', { refChart, datasetIndex })
      this.$refs[refChart].toggleDatasetVisibility(datasetIndex)
    },
    formatCellValue (row, column, cellValue, index) {
      return i18n.n(cellValue ?? 0, 'currency') + ' €'
    },
    readTableFont () {
      const cell = this.$refs.summaryTable?.$el?.querySelector('.el-table__cell .cell')
      if (!cell) return
      const cs = window.getComputedStyle(cell)
      this.tableFontStyle = {
        font: cs.font,
        letterSpacing: parseFloat(cs.letterSpacing) || 0,
        paddingX: (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0)
      }
    },
    textWidthPx (text) {
      this._measureCanvas = this._measureCanvas || document.createElement('canvas')
      const ctx = this._measureCanvas.getContext('2d')
      const { font, letterSpacing, paddingX } = this.tableFontStyle
      ctx.font = font
      const t = String(text ?? '')
      const base = ctx.measureText(t).width
      const spacing = letterSpacing * Math.max(t.length - 1, 0)
      return Math.ceil(base + spacing + paddingX + 6)
    },
    printDashboard () {
      const printArea = this.$el.querySelector('.print-area')
      if (!printArea) return

      const oldIframe = document.getElementById('printeriframe')
      if (oldIframe) oldIframe.remove()

      const clone = printArea.cloneNode(true)
      const originalCanvas = printArea.querySelectorAll('canvas')

      const chartRefs = {
        'chart-panel': this.refPanelChart,
        'chart-doughnut': this.refDoughnutChart,
      }

      clone.querySelectorAll('canvas').forEach((cloneCanvas, index) => {
        if (originalCanvas[index]) {
          const chartId = originalCanvas[index].id
          const isDoughnut = chartId === 'chart-doughnut'
          const chartRef = this.$refs[chartRefs[chartId]]
          const sourceCanvas = chartRef?.trimCanvas() || originalCanvas[index]
          const img = document.createElement('img')
          img.src = sourceCanvas.toDataURL('image/png')
          if (this.isLower1024) {
            img.style.width = isDoughnut ? '45%' : '65%'
          } else {
            img.style.width =isDoughnut ? '45%' : '100%'
          }
          img.style.justifySelf = 'center'
          img.style.alignSelf = 'center'
          cloneCanvas.parentNode.replaceChild(img, cloneCanvas)
        }
      })

      clone.querySelectorAll('.el-table__body-wrapper, .el-table__header-wrapper').forEach(el => {
        el.style.overflow = 'visible'
      })

      const table =clone.getElementsByClassName('el-table')[0]
      table.classList.remove('min-w-max')
      table.style.tableLayout = 'auto'
      table.style.minWidth = '0px'
      table.style.width = '1000px'
      if (table) {
        const colGroup = table.querySelectorAll('colgroup')

        for (let j = 0; j < colGroup.length; j++) {
          const lastColumnIndex = colGroup[j].children.length - 1
          for (let i = 0; i < colGroup[j].children.length; i++) {
            const element = colGroup[j].children[i]
            switch (i) {
              case 2:
                element.setAttribute('width', '270px')
                element.style.width = '270px'
                element.style.minWidth = '0px'
                element.style.padding = '0px'
                break
              case lastColumnIndex:
                element.setAttribute('width', 'auto')
                element.style.width = 'auto'
                element.style.minWidth = '0px'
                element.style.padding = '0px'
                break
              default:
                element.setAttribute('width', '90px')
                element.style.width = '90px'
                element.style.minWidth = '0px'
                element.style.padding = '0px'
                break            
            }
          }
        }
      }

      const iframe = document.createElement('iframe')
      iframe.setAttribute('id', 'printeriframe')
      iframe.setAttribute('width', '768px')
      iframe.style.display = 'none'
      document.body.appendChild(iframe)

      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document

      const allStyles = [...document.querySelectorAll('link[rel="stylesheet"], style')]
        .map(el => el.outerHTML).join('\n')

      iframeDoc.open()
      iframeDoc.write(`<!DOCTYPE html><html><head><meta charset="utf-8">${allStyles}</head><body>${clone.outerHTML}</body></html>`)
      iframeDoc.close()

      

      const printStyle = iframeDoc.createElement('style')
      printStyle.textContent = `
        @page { 
          size: A4; 
          margin: 10mm;
        }
        @page tabla {
          size: A4 landscape; 
          margin: 10mm;
        }
        @media print {
          .print-area { 
            shadow: none !important; 
          }
          .kpi-card {
              break-inside: avoid;
              page-break-inside: avoid;
          }
          #break_table {
            page: tabla;
            break-before: always;
            page-break-before: always;
          }
          .hide-button {
            display: none !important;
          }
        }
        *, *::before, *::after { 
          box-sizing: border-box; 
        }
        html, body {
          width: 100%;
          height: auto;
          margin: 0;
          padding: 0;
          overflow: visible;
          background: white;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .print-area {
          width: 100%;
          padding: 0;
          margin: 0;
        }
        `
      iframeDoc.head.appendChild(printStyle)

      setTimeout(() => {
        try {
          iframe.contentWindow.focus()
          iframe.contentWindow.print()
        } catch (e) {
          console.error('Error al imprimir:', e)
        }
        setTimeout(() => iframe.remove(), 1000)
      }, 300)
    },

  },
}
</script>
<style scoped>
.el-table::v-deep .el-table__cell .cell{
  white-space: normal;
  word-break: break-word;
}
@media print {
  body * {
    visibility: hidden;
  }
  .print-area,
  .print-area * {
    visibility: visible;
  }
  .print-area {
    left: 0;
    top: 0;
    width: 100%;
  }
}
</style>
